import { router } from '@/app/router';
import { paths } from '@/config/paths';
import heroBoyCelebrating from '@/features/hero/assets/avatar-celebrating.svg';
import heroBoySmiling from '@/features/hero/assets/avatar-smiling.svg';
import heroBoyUpset from '@/features/hero/assets/avatar-upset.svg';
import type {
  Cta,
  HeroStateConfig,
} from '@/features/hero/config/hero-states-v2';
import type { HeroData } from '@/features/hero/hooks';
import { downloadFile, getNumberToOrdinalName } from '@/lib/utils';

const makeCta = (heroData: HeroData): Cta => {
  const worksheetUrl = heroData?.latestIncompleteWorksheet?.question_pdf_s3_url;
  const worksheetName = heroData?.latestIncompleteWorksheet?.name;
  if (worksheetUrl && worksheetName) {
    return {
      variant: heroData.shouldShowSecondaryButton ? 'outline' : 'raised-jumbo',
      label: 'Download Worksheet',
      onClick: () => {
        void downloadFile(worksheetUrl, worksheetName ?? 'Worksheet');
      },
    };
  }
  return {
    variant: heroData.shouldShowSecondaryButton ? 'outline' : 'raised-jumbo',
    label: 'Attempt Quizzes',
    onClick: () => void router.navigate(paths.app.brainGym.getHref()),
  };
};

const makeStats = (): string => {
  return 'Keep up the consistency, your hard work pays off.';
};

const makeSubtitle = (heroData: HeroData): string => {
  if (heroData.latestIncompleteWorksheet) {
    const topicName = heroData.latestIncompleteWorksheet.topic?.name;
    if (topicName) {
      return `Download & complete your worksheet on ${topicName}`;
    }
    return `Download & complete your worksheet`;
  }
  return `You've completed all worksheets, now test yourself with fun quizzes!`;
};

const includeName = (heroData: HeroData): string => {
  if (heroData.studentName) {
    return ` ${heroData.studentName}`;
  }
  return '';
};

const HERO_STATES_V2_OFFLINE: HeroStateConfig[] = [
  {
    id: 'STARTED_NEW_MODULE',
    name: 'Started New Module',

    // All current states share the 'primary' group — exactly one resolves
    // per deck (first-match). New stackable states take no group and stack
    // after the resolved primary card.
    group: 'primary',

    isEnabled: heroData => {
      return heroData.hasNewModuleStarted;
    },

    hasError: heroData => {
      return heroData.errorStates.modules;
    },

    getTitle: heroData => {
      const moduleNumber = heroData.currentOngoingModule?.module.number;
      const ordinalModuleNumber = getNumberToOrdinalName(Number(moduleNumber));
      if (ordinalModuleNumber) {
        return `Great Going${includeName(heroData)}! Your <highlight>${ordinalModuleNumber} Module</highlight> Has Kicked Off`;
      }
      return `Great Going${includeName(heroData)}! Your <highlight>New Module</highlight> Has Kicked Off`;
    },

    getSubtitle: heroData => makeSubtitle(heroData),

    getStats: makeStats,

    getCta: heroData => makeCta(heroData),

    getCharacterImage: () => heroBoyCelebrating,
  },

  {
    id: 'ATTENDED_PREVIOUS_SESSION',
    name: 'Attended Previous Session',

    group: 'primary',

    isEnabled: heroData => {
      return heroData.lastSession?.attendance?.status === 'ATTENDED';
    },

    hasError: heroData => {
      return heroData.errorStates.modules;
    },

    getTitle: heroData => {
      return `Great Job${includeName(heroData)}, Attending Your Lesson!`;
    },

    getSubtitle: heroData => makeSubtitle(heroData),

    getStats: makeStats,

    getCta: heroData => makeCta(heroData),

    getCharacterImage: () => heroBoySmiling,
  },

  {
    id: 'MISSED_1_SESSION',
    name: 'Missed 1 Session',

    group: 'primary',

    isEnabled: heroData => {
      return heroData.lastSession?.attendance?.status === 'MISSED';
    },

    hasError: heroData => {
      return heroData.errorStates.modules;
    },

    getTitle: heroData => {
      return `Uh-Oh${includeName(heroData)}! You <highlight>Missed</highlight> A Lesson`;
    },

    getSubtitle: () => {
      return `Stay consistent by attending your upcoming lessons`;
    },

    getCta: heroData => makeCta(heroData),

    getCharacterImage: () => heroBoyUpset,
  },

  {
    id: 'DEFAULT',
    name: 'Default',

    // Catch-all: without this flag its always-true isEnabled would put it in
    // every deck.
    fallback: true,

    isEnabled: () => true,

    getTitle: heroData => {
      return `Hi${includeName(heroData)}, Welcome to <highlight>Bhanzu Learning Center!</highlight>`;
    },

    getSubtitle: () => 'Your Bhanzu journey starts here',

    getCharacterImage: () => heroBoyCelebrating,
  },
];

export const DEFAULT_HERO_STATE_OFFLINE: HeroStateConfig = {
  id: 'DEFAULT',
  name: 'Default',
  isEnabled: () => true,
  getTitle: () =>
    'Hi, Welcome to <highlight>Bhanzu Learning Center!</highlight>',
  getSubtitle: () => 'Your Bhanzu journey starts here',
  getCharacterImage: () => heroBoyCelebrating,
};

export default HERO_STATES_V2_OFFLINE;
